/**
 * The invitation acceptance page — §6.14c, §6.15a, §6.15b.
 *
 * §6.14c: "The link opens **one page on the platform's own domain** carrying
 * everything the decision needs, in full, before any accept or decline control."
 *
 * So the accept button is at the bottom, after everything, and there is no
 * step before this one. A page that asked somebody to sign in first would have
 * them deciding before seeing, and a page that put the button above the terms
 * would have most of them deciding without reading.
 *
 * It renders before any session exists, which is why it is a separate entry
 * rather than a tab: whoever holds the token is who this is for.
 */

import { useState } from 'react';

import { Callout, Empty, Field, Loading, Panel, PasswordInput, PASSWORD_MIN_LENGTH, TextInput, explainFailure, formatDate, kitText, logoSrc, useLoad } from '@affdice/portal-kit';

import type { AffiliateApi } from './client.ts';
import { t } from './messages.ts';

export interface Offer {
  readonly operator_legal_name: string;
  readonly operator_trading_name: string;
  readonly brands: readonly string[];
  /** The one brand's logo, as an API path; null when it has none or the offer spans brands. */
  readonly brand_logo_url?: string | null;
  readonly expires_at: string;
  readonly terms: string;
  readonly restrictions: readonly string[];
  readonly will_see: readonly string[];
  readonly will_not_see: readonly string[];
  readonly starts_empty: string;
}

/** The offered brand's logo; absent or failing to load, nothing is drawn. */
function InviteLogo({ src, name }: { src: string | undefined; name: string }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return null;
  return (
    <span className="brand-logo-tile is-large">
      <img src={src} alt={t('brand.logoAlt', { brand: name })} onError={() => setFailed(true)} />
    </span>
  );
}

export function InvitationPage({ api, token }: { api: AffiliateApi; token: string }) {
  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [failure, setFailure] = useState<unknown>(null);

  const loaded = useLoad(() => api.invitation(token), [token]);

  if (loaded.state === 'loading') return <div className="main"><Loading rows={3} /></div>;
  if (loaded.state === 'error') {
    return (
      <div className="main">
        <Callout tone="error">{explainFailure(loaded.error).message}</Callout>
      </div>
    );
  }

  if (!loaded.value.valid || !loaded.value.offer) {
    // §6.14a — expired, used, superseded and never-existed all answer alike.
    return (
      <div className="main">
        <div className="page-head"><h1>{t('invite.title')}</h1></div>
        <Callout tone="caution">{loaded.value.message}</Callout>
      </div>
    );
  }

  const offer = loaded.value.offer;
  // Defaults to asking. A server that did not say leaves somebody with no way
  // in, which is worse than one extra field.
  const needsPassword = loaded.value.needs_password !== false;

  if (done) {
    // Drop `/invite` so the page lands on sign-in.
    const signInPath = location.pathname.replace(/\/invite\/?$/, '') || '/';
    const portalUrl = location.origin + (signInPath === '/' ? '' : signInPath);

    return (
      <div className="main signin">
        <div className="page-head"><h1>{t('invite.joined')}</h1></div>
        <Panel title={t('invite.next')}>
          <Callout>{done}</Callout>
          <p className="link-note">
            {needsPassword
              ? t('invite.next.newPassword')
              : t('invite.next.existingPassword')}
          </p>
          <p className="link-note">
            {t('invite.portalUrl')} <strong>{portalUrl}</strong>
          </p>
          <div className="form-actions">
            <a className="primary" href={signInPath}>
              {t('invite.signIn')}
            </a>
          </div>
        </Panel>
      </div>
    );
  }

  return (
    <div className="main">
      <div className="page-head">
        <h1>
          {offer.brands.length === 1
            ? t('invite.headline.brand', { brand: offer.brands[0] ?? '' })
            : t('invite.headline.operator', { operator: offer.operator_trading_name })}
        </h1>
        <span className="updated">
          {t('invite.openUntil', { date: formatDate(offer.expires_at) })}
        </span>
      </div>

      <Panel title={t('invite.contractingWith')}>
        {offer.brands.length === 1 ? (
          <div className="invite-brand">
            <InviteLogo src={logoSrc(api.apiBaseUrl, offer.brand_logo_url)} name={offer.brands[0] ?? ''} />
            <p className="terms-summary">{offer.brands[0]}</p>
          </div>
        ) : (
          <>
            <p className="terms-summary">{offer.operator_legal_name}</p>
            <p className="link-note">
              {t('invite.programme', { operator: offer.operator_trading_name })}
            </p>
          </>
        )}
      </Panel>

      {/* §6.15b — "Consent that does not state its scope is not consent." Both
          halves, side by side, because the reassuring half alone is marketing. */}
      <Panel title={t('invite.willSee')}>
        <ul className="scope">
          {offer.will_see.map((s) => <li key={s}>{s}</li>)}
        </ul>
      </Panel>

      <Panel title={t('invite.willNotSee')}>
        <ul className="scope will-not">
          {offer.will_not_see.map((s) => <li key={s}>{s}</li>)}
        </ul>
      </Panel>

      {/* §6.15a — nothing carries across, said before they decide rather than
          discovered afterwards when their tier resets. */}
      <Callout>{offer.starts_empty}</Callout>

      <Callout>{offer.terms}</Callout>

      {offer.restrictions.map((r) => (
        <Callout key={r}>{r}</Callout>
      ))}

      {/* Last. §6.14c puts every control after everything the decision needs. */}
      <Panel title={t('invite.join')}>
        <div className="form">
          {failure ? (
            <Callout tone="error">{explainFailure(failure).message}</Callout>
          ) : null}

          <div className="form-row">
            <Field label={t('invite.field.name')}>
              <TextInput value={name} onChange={setName} placeholder="Northline Media" />
            </Field>
            <Field label={t('invite.field.country')} hint={t('invite.field.country.hint')}>
              <TextInput value={country} onChange={setCountry} placeholder="GB" />
            </Field>
          </div>

          {/* The part that was missing entirely: accepting created a profile
              and no way to sign in to it. Holding this link is what proves the
              address, so they choose their own password rather than being sent
              one — §3.7's must-change is for passwords two people know. */}
          {needsPassword ? (
            <Field
              label={kitText('password.title')}
              hint={t('invite.field.password.hint')}
            >
              <PasswordInput
                value={password}
                onChange={setPassword}
                autoComplete="new-password"
                minLength={PASSWORD_MIN_LENGTH}
              />
            </Field>
          ) : (
            // §2.3b — one identity across operators. Offering to set a new
            // password here would let whoever holds an invitation overwrite an
            // existing affiliate's, which is a takeover dressed as onboarding.
            <Callout>{t('invite.existingAccount')}</Callout>
          )}

          <div className="form-actions">
            <button
              className="primary"
              disabled={
                busy ||
                name.trim() === '' ||
                country.trim().length !== 2 ||
                (needsPassword && password.length < PASSWORD_MIN_LENGTH)
              }
              onClick={() => {
                setBusy(true);
                setFailure(null);
                api
                  .acceptInvitation(token, {
                    display_name: name,
                    country: country.toUpperCase(),
                    ...(needsPassword ? { password } : {}),
                  })
                  .then((r) =>
                    setDone(
                      r.message ?? t('invite.accepted'),
                    ),
                  )
                  .catch((e: unknown) => setFailure(e))
                  .finally(() => setBusy(false));
              }}
            >
              {busy ? t('invite.busy') : t('invite.accept')}
            </button>
          </div>
        </div>
      </Panel>

      <Empty>{t('invite.closingDeclines')}</Empty>
    </div>
  );
}
